import { useState, useMemo } from 'react';
import { fetchRegisterMeetings } from '../api/suggestionService';
import { toast } from 'react-toastify';
import { filterMeetingsForTemplate } from '../utils/formUtils';
import { ONLINE_VOTING_TEMPLATE_IDS } from '../utils/constants';

export const useMeetings = (formData, setFormData, setErrors, isOnlineVotingTemplate, dependencies) => {
    const { validateSendingOptionWithSendToOptions, setEventDateSource } = dependencies;
    const [allMeetings, setAllMeetings] = useState([]);
    const [isLoadingMeetings, setIsLoadingMeetings] = useState(false);
    const [showMeetings, setShowMeetings] = useState(true);
    const [meetingInput, setMeetingInput] = useState('');
    const [meetingSuggestions, setMeetingSuggestions] = useState([]);
    const [showMeetingSuggestions, setShowMeetingSuggestions] = useState(false);
    const [selectedMeetingSuggestionIndex, setSelectedMeetingSuggestionIndex] = useState(-1);
    const [includeFinalised, setIncludeFinalised] = useState(false);

    const meetings = useMemo(() => {
        const isMinutes = formData?.issueType === 'minutes';
        if (isMinutes && includeFinalised) return allMeetings;
        return allMeetings.filter(m => m.lStatusID !== 6);
    }, [allMeetings, includeFinalised, formData?.issueType]);

    const fetchMeetingsForPlan = async (planNumber, databaseName) => {
        if (!planNumber || !databaseName) return;
        setIsLoadingMeetings(true);
        try {
            const response = await fetchRegisterMeetings(planNumber, databaseName, true);
            setAllMeetings(response.data || []);
            setShowMeetings(true);
        } catch (error) {
            console.error('Error fetching meetings:', error);
            toast.error('Failed to fetch meetings');
            setAllMeetings([]);
            setShowMeetings(false);
        } finally {
            setIsLoadingMeetings(false);
        }
    };

    const handleIncludeFinalisedChange = (val) => {
        setIncludeFinalised(val);
    };

    const handleMeetingInputChange = (e) => {
        const value = e.target.value;
        setMeetingInput(value);
        
        setFormData(prevData => ({ ...prevData, selectedMeetingId: null, meetingName:'', online_voting_open_for_voting: null, online_voting_show_on_portal: null }));
        
        if (value.length >= 1) {
            const filtered = filterMeetingsForTemplate(meetings, isOnlineVotingTemplate).filter(meeting => {
                const meetingId = meeting.lMeetingRegisterID.toString();
                const meetingTypeDescription = meeting.MeetingTypeDescription || '';
                const meetingDate = meeting.dMeeting ? new Date(meeting.dMeeting).toLocaleDateString('en-AU', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                }) : '';
                const meetingTime = meeting.sMeetingTime || '';

                return meetingId.includes(value) ||
                       meetingTypeDescription.toLowerCase().includes(value.toLowerCase()) ||
                       meetingDate.includes(value) ||
                       meetingTime.toLowerCase().includes(value.toLowerCase());
            });
            
            setMeetingSuggestions(filtered);
            setShowMeetingSuggestions(filtered.length > 0);
            setSelectedMeetingSuggestionIndex(-1);
            
            setErrors(prev => ({
                ...prev,
                meetingSuggestion: value.trim() !== '',
            }));
        } else {
            setMeetingSuggestions([]);
            setShowMeetingSuggestions(false);
            setSelectedMeetingSuggestionIndex(-1);
            setFormData(prevData => ({ ...prevData, selectedMeetingId: null, meetingName:'', online_voting_open_for_voting: null, online_voting_show_on_portal: null }));
            setEventDateSource?.(null);
            setErrors(prev => ({ ...prev, meetingSuggestion: false }));
        }
    };

    const handleMeetingSelect = (meeting) => {
        const meetingId = meeting.lMeetingRegisterID;
        const meetingTypeDescription = meeting.MeetingTypeDescription || meetingId.toString();
        const meetingDate = meeting.dMeeting ? (() => { const [y,m,d] = meeting.dMeeting.split('T')[0].split('-'); return `${parseInt(d)}/${parseInt(m)}/${y}`; })() : 'N/A';
        const meetingTime = meeting.sMeetingTime || '';

        const meetingDisplayName = `${meetingTypeDescription} - ${meetingDate} ${meetingTime}`;
        setMeetingInput(meetingDisplayName);

        let parsedEventDateTime = null;
        if (meeting.dMeeting) {
            try {
                const dateStr = meeting.dMeeting.split('T')[0];
                let timeStr = '00:00';

                if (meeting.sMeetingTime) {
                    const timePart = meeting.sMeetingTime.trim();
                    const timeMatch = timePart.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
                    if (timeMatch) {
                        let hours = parseInt(timeMatch[1]);
                        const minutes = timeMatch[2];
                        const meridiem = timeMatch[3];

                        if (meridiem) {
                            if (meridiem.toUpperCase() === 'PM' && hours !== 12) {
                                hours += 12;
                            } else if (meridiem.toUpperCase() === 'AM' && hours === 12) {
                                hours = 0;
                            }
                        }
                        timeStr = `${hours.toString().padStart(2, '0')}:${minutes}`;
                    }
                }

                const combinedDateTime = new Date(`${dateStr}T${timeStr}`);
                if (!isNaN(combinedDateTime.getTime())) {
                    parsedEventDateTime = combinedDateTime;
                }
            } catch (error) {
                console.error('Error parsing meeting date/time:', error);
            }
        }

        const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        const dateForDocName = meeting.dMeeting
            ? (() => { const [y,m,d] = meeting.dMeeting.split('T')[0].split('-'); return `${parseInt(d)} ${MONTH_NAMES[parseInt(m) - 1]} ${y}`; })()
            : '';
        const autoDocName = (prevDocName, date) => {
            const stripped = (prevDocName || '').replace(/ \d{1,2} (?:January|February|March|April|May|June|July|August|September|October|November|December) \d{4}$/, '').trimEnd();
            return `${stripped}${date ? ' ' + date : ''}`;
        };

        const isOnlineVoting = ONLINE_VOTING_TEMPLATE_IDS.includes(Number(formData.smata_config_id));

        setFormData(prevData => {
            const updates = {
                ...prevData,
                selectedMeetingId: meetingId,
                meetingName: meetingDisplayName,
                ...(prevData.issueType ? { custom_document_name: autoDocName(prevData.custom_document_name, dateForDocName), sendTo: 'owners' } : {}),
                ...(isOnlineVoting ? {
                    online_voting_open_for_voting: true,
                    online_voting_show_on_portal: true
                } : {})
            };

            if (parsedEventDateTime) {
                const year = parsedEventDateTime.getFullYear();
                const month = String(parsedEventDateTime.getMonth() + 1).padStart(2, '0');
                const day = String(parsedEventDateTime.getDate()).padStart(2, '0');
                const hours = String(parsedEventDateTime.getHours()).padStart(2, '0');
                const minutes = String(parsedEventDateTime.getMinutes()).padStart(2, '0');
                const datetimeValue = `${year}-${month}-${day}T${hours}:${minutes}`;

                updates.mergeFieldsData = {
                    ...prevData.mergeFieldsData,
                    event: datetimeValue
                };
                setEventDateSource?.('strata');

                if (prevData.issueType === 'notices' && prevData.sendReminders === 'yes') {
                    const reminderDate = new Date(parsedEventDateTime);
                    const daysBefore = isOnlineVoting ? 2 : 1;
                    reminderDate.setDate(reminderDate.getDate() - daysBefore);
                    reminderDate.setHours(10, 0, 0, 0);
                    updates.reminderDate = reminderDate.toString();
                }
            }

            return updates;
        });

        setMeetingSuggestions([]);
        setShowMeetingSuggestions(false);
        setErrors(prev => ({ ...prev, meetingSuggestion: false, sendTo: false }));

        if (formData.issueType && validateSendingOptionWithSendToOptions) {
            validateSendingOptionWithSendToOptions(formData.smata_config_id, 'owners', formData.lotsUserTypes);
        }
    };

    return {
        meetings,
        setMeetings: setAllMeetings,
        isLoadingMeetings,
        setIsLoadingMeetings,
        showMeetings,
        setShowMeetings,
        meetingInput,
        setMeetingInput,
        meetingSuggestions,
        setMeetingSuggestions,
        showMeetingSuggestions,
        setShowMeetingSuggestions,
        selectedMeetingSuggestionIndex,
        setSelectedMeetingSuggestionIndex,
        includeFinalised,
        setIncludeFinalised,
        fetchMeetingsForPlan,
        handleIncludeFinalisedChange,
        handleMeetingInputChange,
        handleMeetingSelect
    };
};
